import { Link } from "react-router-dom";
import { ArrowRight, Check, ChevronRight, Phone } from "lucide-react";
import { COMPANY, wa, WA_MESSAGES } from "@/data/site";
import { useQuote } from "@/components/QuoteModal";
import { BrandImage } from "@/components/BrandImage";
import { Reveal } from "@/components/Reveal";
import { track } from "@/lib/analytics";

export const CARD_MAX_W = { 3: "23rem", 4: "18rem", 5: "14.5rem" };

export const splitRows = (items, cols) => {
  const rem = items.length % cols;
  if (rem === 0 || items.length <= cols) return [items, []];
  return [items.slice(0, items.length - rem), items.slice(items.length - rem)];
};

export const SectionHead = ({ eyebrow, title, text, center = false, light = false }) => (
  <Reveal className={`max-w-3xl ${center ? "mx-auto text-center" : ""} mb-8 md:mb-12`}>
    {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
    <h2 className={`h2-display ${light ? "!text-white" : ""}`}>{title}</h2>
    {text && <p className={`lead-text mt-4 ${light ? "!text-white/70" : ""}`}>{text}</p>}
  </Reveal>
);

export const PageHero = ({ eyebrow, title, intro, crumbs = [], image, imgKey, alt, badge }) => (
  <section className="bg-white border-b border-cts-line">
    <div className="container-site section-pad !pb-10 !pt-10 md:!pt-14">
      <nav aria-label="Breadcrumb" className="mb-6" data-testid="breadcrumb-nav">
        <ol className="flex flex-wrap items-center gap-1.5 text-xs font-body text-cts-charcoal/70">
          <li>
            <Link to="/" data-testid="breadcrumb-home" className="hover:text-cts-red transition-colors">Home</Link>
          </li>
          {crumbs.map((c) => (
            <li key={c.label} className="flex items-center gap-1.5">
              <ChevronRight size={12} className="text-cts-red" />
              {c.to ? (
                <Link to={c.to} data-testid={`breadcrumb-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="hover:text-cts-red transition-colors">{c.label}</Link>
              ) : (
                <span className="text-cts-ink font-semibold" data-testid={`breadcrumb-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-current`} aria-current="page">{c.label}</span>
              )}
            </li>
          ))}
        </ol>
      </nav>
      <div className={`grid gap-10 items-center ${image ? "lg:grid-cols-2" : "max-w-3xl"}`}>
        <Reveal>
          {badge && (
            <span className="inline-block font-heading font-bold text-[11px] tracking-[0.18em] uppercase bg-cts-red text-white rounded px-3 py-1.5 mb-4">
              {badge}
            </span>
          )}
          {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
          <h1 className="h1-display">{title}</h1>
          {intro && <p className="lead-text mt-5">{intro}</p>}
        </Reveal>
        {image && (
          <Reveal delay={120}>
            <div className="relative rounded-xl overflow-hidden shadow-[0_30px_60px_-30px_rgba(23,27,30,0.35)]">
              <BrandImage src={image} imgKey={imgKey} alt={alt || title} eager className="w-full h-72 lg:h-96" testId="page-hero-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-cts-ink/25 via-transparent to-transparent pointer-events-none" aria-hidden="true" />
            </div>
          </Reveal>
        )}
      </div>
    </div>
  </section>
);

export const CtaBand = ({
  title = "Tell Us What You Need",
  text = "Whether you need a computer, printer, networking solution, CCTV installation or technical service, we're here to help.",
  primaryLabel = "GET A QUOTE",
  requirement,
  waMsg = WA_MESSAGES.general,
  showWhatsApp = true,
}) => {
  const { openQuote } = useQuote();
  return (
    <section className="bg-cts-charcoal">
      <div className="container-site section-pad text-center">
        <Reveal>
          <h2 className="h2-display !text-white">{title}</h2>
          <p className="lead-text !text-white/70 max-w-2xl mx-auto mt-4">{text}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8">
            <button onClick={() => openQuote(requirement)} data-testid="ctaband-quote-btn" className="btn-primary w-full sm:w-auto">
              {primaryLabel}
            </button>
            {showWhatsApp && (
              <a
                href={wa(waMsg)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("whatsapp_click", { location: "cta_band" })}
                data-testid="ctaband-whatsapp-btn"
                className="btn bg-transparent border border-white/50 text-white hover:border-cts-red hover:text-cts-red w-full sm:w-auto"
              >
                WHATSAPP US
              </a>
            )}
            <a
              href={COMPANY.phoneHref}
              onClick={() => track("phone_click", { location: "cta_band" })}
              data-testid="ctaband-call-btn"
              className="inline-flex items-center gap-2 font-heading font-bold text-sm text-white/80 hover:text-white transition-colors"
            >
              <Phone size={16} /> {COMPANY.phone}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Card = ({ item, i, light, openQuote }) => {
  const Icon = item.icon;
  const inner = (
    <>
      {(Icon || item.badge) && (
        <div className="flex items-start justify-between mb-4">
          {Icon ? (
            <span className={`inline-flex items-center justify-center w-12 h-12 rounded-md ${light ? "bg-white/10" : "bg-cts-redlight"}`}>
              <Icon size={24} strokeWidth={1.75} className="text-cts-red" />
            </span>
          ) : (
            <span />
          )}
          {item.badge && (
            <span className="font-heading font-bold text-[10px] tracking-[0.14em] uppercase bg-cts-redlight text-cts-red rounded px-2.5 py-1">
              {item.badge}
            </span>
          )}
        </div>
      )}
      <h4 className={`font-heading font-bold text-lg mb-2 ${light ? "text-white" : "text-cts-ink"}`}>{item.title}</h4>
      <p className={`text-[15px] leading-relaxed ${light ? "text-white/70" : "text-cts-charcoal"}`}>{item.text}</p>
      {(item.to || item.quoteReq || item.cta) && (
        <span className="link-arrow mt-4">
          {item.cta || (item.quoteReq ? "ENQUIRE" : "EXPLORE")} <ArrowRight size={15} />
        </span>
      )}
    </>
  );
  const cls = `card-base group h-full p-6 ${light ? "!bg-[#3A444B] !border-white/10 hover:!border-cts-red" : ""}`;
  const tid = `card-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <Reveal delay={i * 60}>
      {item.to ? (
        <Link to={item.to} data-testid={tid} className={`${cls} block`}>
          {inner}
        </Link>
      ) : item.quoteReq ? (
        <button onClick={() => openQuote(item.quoteReq)} data-testid={tid} className={`${cls} w-full text-left`}>
          {inner}
        </button>
      ) : (
        <div data-testid={tid} className={cls}>
          {inner}
        </div>
      )}
    </Reveal>
  );
};

export const CardsGrid = ({ items, cols = 3, heading, sub, light = false }) => {
  const { openQuote } = useQuote();
  const colClass = cols === 5 ? "lg:grid-cols-5" : cols === 4 ? "lg:grid-cols-4" : cols === 2 ? "lg:grid-cols-2" : "lg:grid-cols-3";
  const [mainItems, lastRow] = splitRows(items, cols);
  return (
    <div>
      {heading && <h3 className={`h3-display mb-2 ${light ? "!text-white" : ""}`}>{heading}</h3>}
      {sub && <p className={`lead-text mb-8 ${light ? "!text-white/70" : ""}`}>{sub}</p>}
      <div className={`grid gap-5 sm:grid-cols-2 ${colClass}`}>
        {mainItems.map((item, i) => (
          <Card key={item.title} item={item} i={i} light={light} openQuote={openQuote} />
        ))}
      </div>
      {lastRow.length > 0 && (
        <div className="flex flex-wrap justify-center gap-5 mt-5">
          {lastRow.map((item, i) => (
            <div key={item.title} className="w-full sm:w-[calc(50%-10px)]" style={{ maxWidth: CARD_MAX_W[cols] }}>
              <Card item={item} i={mainItems.length + i} light={light} openQuote={openQuote} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const StepsRow = ({ steps, heading, light = false }) => (
  <div>
    {heading && <h3 className={`h3-display mb-8 ${light ? "!text-white" : ""}`}>{heading}</h3>}
    <ol className="flex flex-col sm:flex-row sm:flex-wrap gap-4 sm:gap-0 sm:items-stretch">
      {steps.map((step, i) => (
        <li key={step} className="flex items-center gap-3 sm:gap-0">
          <Reveal delay={i * 70} className="flex items-center gap-3">
            <span className="font-mono font-bold text-cts-red text-xl w-9 shrink-0">{String(i + 1).padStart(2, "0")}</span>
            <span className={`font-heading font-bold text-sm uppercase tracking-wide ${light ? "text-white" : "text-cts-ink"}`}>{step}</span>
          </Reveal>
          {i < steps.length - 1 && (
            <ArrowRight size={18} className="hidden sm:block text-cts-red mx-4 shrink-0" aria-hidden="true" />
          )}
        </li>
      ))}
    </ol>
  </div>
);

export const CheckList = ({ items, light = false }) => (
  <ul className="grid gap-3 sm:grid-cols-2">
    {items.map((item) => (
      <li key={item} className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-cts-redlight shrink-0">
          <Check size={14} strokeWidth={2.5} className="text-cts-red" />
        </span>
        <span className={`text-[15px] leading-relaxed ${light ? "text-white/80" : "text-cts-charcoal"}`}>{item}</span>
      </li>
    ))}
  </ul>
);

export const SplitFeature = ({ eyebrow, title, paragraphs = [], points, image, imgKey, alt, reverse = false, children, testId }) => (
  <div className={`grid gap-10 lg:grid-cols-2 lg:gap-16 items-center`} data-testid={testId}>
    <Reveal className={reverse ? "lg:order-2" : ""}>
      <div className="relative rounded-xl overflow-hidden border border-cts-line shadow-[0_30px_60px_-30px_rgba(23,27,30,0.35)]">
        <BrandImage src={image} imgKey={imgKey} alt={alt || title} className="img-zoom relative w-full h-72 md:h-96" testId={testId ? `${testId}-image` : undefined} />
        <div className="absolute inset-0 bg-gradient-to-t from-cts-ink/20 via-transparent to-transparent pointer-events-none" aria-hidden="true" />
      </div>
    </Reveal>
    <Reveal delay={120} className={reverse ? "lg:order-1" : ""}>
      {eyebrow && <p className="eyebrow mb-3">{eyebrow}</p>}
      <h2 className="h2-display">{title}</h2>
      {paragraphs.map((p) => (
        <p key={p.slice(0, 32)} className="lead-text mt-4">{p}</p>
      ))}
      {points && <div className="mt-6"><CheckList items={points} /></div>}
      {children && <div className="mt-7">{children}</div>}
    </Reveal>
  </div>
);
